// OrbitalX Brand Colors - Based on logo color #52de45
// These constants are for programmatic use; prefer Tailwind classes when possible

export const THEME_COLORS = {
  // Primary brand green from logo
  PRIMARY: '#52de45',

  // Secondary brand purple/magenta (from hottest leads signal bars)
  SECONDARY: '#d83ce7',

  // Derived green shades for consistent theming
  GREEN: {
    // Light variations for backgrounds
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',

    // Our primary brand color
    500: '#52de45', // Primary brand color
    600: '#16a34a', // Darker green for hover states
    700: '#15803d',

    // Dark variations for dark mode
    800: '#166534',
    900: '#14532d',
    950: '#052e16',
  },

  // Derived purple/magenta shades for consistent theming (from hottest leads signal bars)
  PURPLE: {
    // Light variations
    50: '#fdf4ff',
    100: '#fae8ff',
    200: '#f5d0fe',
    300: '#f0abfc',
    400: '#f69bff', // Lightest purple from signal bars

    // Our secondary brand colors
    500: '#d83ce7', // Primary purple from signal bars
    600: '#c026d3',
    700: '#a21caf',

    // Dark variations
    800: '#881f92', // Darkest purple from signal bars
    900: '#701a75',
    950: '#4a044e',
  },

  // Background opacity variations (for programmatic use)
  BACKGROUNDS: {
    // Light mode
    LIGHT_SUBTLE: 'rgba(82, 222, 69, 0.1)',    // 10% opacity
    LIGHT_MEDIUM: 'rgba(82, 222, 69, 0.15)',   // 15% opacity
    LIGHT_STRONG: 'rgba(82, 222, 69, 0.2)',    // 20% opacity

    // Dark mode
    DARK_SUBTLE: 'rgba(82, 222, 69, 0.1)',     // 10% opacity
    DARK_MEDIUM: 'rgba(82, 222, 69, 0.15)',    // 15% opacity
    DARK_STRONG: 'rgba(82, 222, 69, 0.2)',     // 20% opacity
  },

  // Border opacity variations (for programmatic use)
  BORDERS: {
    LIGHT: 'rgba(82, 222, 69, 0.2)',
    DARK: 'rgba(82, 222, 69, 0.3)',
  }
} as const

// Tailwind class mappings - use these for consistent brand colors
export const BRAND_CLASSES = {
  // Background classes
  BG: {
    PRIMARY: 'bg-primary-500',
    PRIMARY_HOVER: 'hover:bg-primary-600',
    PRIMARY_LIGHT: 'bg-primary-100 dark:bg-primary-900/20',
    PRIMARY_SUBTLE: 'bg-primary-50 dark:bg-primary-950/50',
  },

  // Text classes
  TEXT: {
    PRIMARY: 'text-primary-600 dark:text-primary-400',
    PRIMARY_STRONG: 'text-primary-700 dark:text-primary-300',
    ON_PRIMARY: 'text-white',
  },

  // Border classes
  BORDER: {
    PRIMARY: 'border-primary-200 dark:border-primary-800',
    PRIMARY_STRONG: 'border-primary-300 dark:border-primary-700',
  },

  // Focus/Ring classes
  FOCUS: {
    PRIMARY: 'focus:ring-primary-500 dark:focus:ring-primary-400',
    BORDER: 'focus:border-primary-500 dark:focus:border-primary-400',
  },

  // Form input states
  INPUT: {
    // Normal state (default)
    NORMAL: 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
    NORMAL_BORDER: 'border-slate-300 dark:border-slate-600',
    NORMAL_BG: 'bg-white dark:bg-slate-700',
    NORMAL_TEXT: 'text-slate-900 dark:text-white',
    PLACEHOLDER: 'placeholder-slate-500',

    // Error state
    ERROR: 'border-red-300 focus:ring-red-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
    ERROR_BORDER: 'border-red-300',
    ERROR_RING: 'focus:ring-red-500',
    ERROR_TEXT: 'text-red-600 dark:text-red-400',

    // Helper/description text
    HELPER: 'text-slate-500 dark:text-slate-400',
    HELPER_WARNING: 'text-amber-600 dark:text-amber-400',

    // Required indicator
    REQUIRED: 'text-red-500',

    // Label
    LABEL: 'text-slate-700 dark:text-slate-300',
  },

  // Button presets
  BUTTON: {
    PRIMARY: 'bg-primary-600 hover:bg-primary-700 text-white',
    PRIMARY_OUTLINE: 'border border-primary-600 text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:border-primary-400 dark:hover:bg-primary-950/50',
    PRIMARY_GHOST: 'text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-950/50',
  },

  // Status badge/pill colors (bg + text for rounded pills)
  STATUS: {
    DRAFT: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    SUCCESS: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    INFO: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    WARNING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    SYNC_ERROR: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    DEFAULT: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  },

  // Accent text colors for icons and decorative elements
  ACCENT: {
    PURPLE: 'text-purple-500 dark:text-purple-400',
    VIOLET: 'text-violet-500 dark:text-violet-400',
    INDIGO: 'text-indigo-500 dark:text-indigo-400',
    BLUE: 'text-blue-500 dark:text-blue-400',
    TEAL: 'text-teal-500 dark:text-teal-400',
    AMBER: 'text-amber-500 dark:text-amber-400',
    RED: 'text-red-500 dark:text-red-400',
    SLATE: 'text-slate-500 dark:text-slate-400',
  },
} as const

// Utility function to get CSS custom property value
export function getCSSCustomProperty(property: string): string {
  if (typeof window !== 'undefined') {
    return getComputedStyle(document.documentElement).getPropertyValue(property)
  }
  return ''
}

// Get the primary brand color (useful for dynamic styling)
export function getPrimaryColor(): string {
  return getCSSCustomProperty('--color-primary-500') || THEME_COLORS.PRIMARY
}
